import React, { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import {
  Plus,
  RefreshCw,
  Server,
  Plug,
  PlugZap,
  RotateCw,
  Pencil,
  Trash2,
  ShieldCheck,
  Globe,
  Key,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  Search,
  Settings,
  Save,
  FlaskConical,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import {
  GatewayServerConfigApi,
  GatewayAuthConfigApi,
  GatewayStsApi,
} from "../Api/gatewayApi";
import { McpServerConfig } from "../interfaces/gatewayInterfaces";
import {
  formatTimestamp,
  timeAgo,
  detectIdpType,
  apiError,
  TOKEN_CLASSIFICATION_OPTIONS,
} from "../utils/gatewayUtils";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Skeleton } from "../../../ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";


// Backend ServerConfigRequest is HTTP-only (url is @NotBlank ^https?://); no stdio command/args/env.
type AuthMode = "none" | "bearer" | "apikey" | "urlparam" | "custom";

interface ServerFormState {
  name: string;
  url: string;
  timeoutSeconds: number;
  enabled: boolean;
  autoConnect: boolean;
  authMode: AuthMode;
  authToken: string;
  authHeaderName: string;
  authHeaderValue: string;
  authParamName: string;
  authParamValue: string;
  headers: string;
  serverConfig: string;
}

const EMPTY_SERVER_FORM: ServerFormState = {
  name: "",
  url: "",
  timeoutSeconds: 30,
  enabled: true,
  autoConnect: true,
  authMode: "none",
  authToken: "",
  authHeaderName: "x-api-key",
  authHeaderValue: "",
  authParamName: "apikey",
  authParamValue: "",
  headers: "",
  serverConfig: "",
};

interface AuthFormState {
  idpDisplayName: string;
  authMode: string;
  issuerUri: string;
  jwksUri: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  tokenClassificationMode: string;
  gracePeriodMinutes: string;
  supportedScopes: string;
}

const EMPTY_AUTH_FORM: AuthFormState = {
  idpDisplayName: "",
  authMode: "oauth2",
  issuerUri: "",
  jwksUri: "",
  authorizationEndpoint: "",
  tokenEndpoint: "",
  tokenClassificationMode: "jwt-signals",
  gracePeriodMinutes: "30",
  supportedScopes: "",
};

interface ValidationResult {
  state: "idle" | "loading" | "success" | "error";
  message?: string;
  jwksKeyCount?: number;
  jwksLatencyMs?: number;
}


function idpIcon(issuerUri: string | undefined): React.ReactNode {
  const name = detectIdpType(issuerUri);
  switch (name) {
    case "Keycloak":
      return <Key className="w-4 h-4" />;
    case "Azure AD":
      return <ShieldCheck className="w-4 h-4" />;
    case "Okta":
    case "Auth0":
      return <ShieldCheck className="w-4 h-4" />;
    case "Google":
      return <Globe className="w-4 h-4" />;
    default:
      return <ShieldCheck className="w-4 h-4" />;
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

interface ServerCardProps {
  server: McpServerConfig;
  onEdit: (server: McpServerConfig) => void;
  onDelete: (server: McpServerConfig) => void;
  onConnect: (name: string) => void;
  onReconnect: (name: string) => void;
  onToggleEnabled: (name: string, isEnabled: boolean) => void;
  actionLoading: string | null;
}

function ServerCard({
  server,
  onEdit,
  onDelete,
  onConnect,
  onReconnect,
  onToggleEnabled,
  actionLoading,
}: ServerCardProps) {
  const { toast } = useToast();
  const isConnected = server.connected === true;
  const isEnabled = server.enabled !== false;
  const name = server.name;
  const isBusy = actionLoading === name;
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  // Probe a SAVED server with its stored, decrypted config (no persist / no connect).
  const handleTestSaved = async () => {
    setTesting(true);
    try {
      const r = await GatewayServerConfigApi.testSaved(name);
      if (r?.ok) {
        const parts = [`${r.toolCount ?? 0} tools`];
        if (r.resourceCount) parts.push(`${r.resourceCount} res`);
        if (r.promptCount) parts.push(`${r.promptCount} prompts`);
        toast({ title: `${name}: OK`, description: `${parts.join(", ")} (${r.latencyMs}ms)` });
      } else {
        toast({ title: `${name}: unreachable`, description: r?.error || "Connection failed", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Test failed", description: apiError(e), variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };
  const protocolType = (server.type || server.transportType || "HTTP").toUpperCase();
  // ServerConfigResponse exposes connectedAt (not lastActivity)
  const connectedLabel = (server as any).connectedAt ? timeAgo((server as any).connectedAt) : "—";
  const headers =
    server.headers && Object.keys(server.headers).length > 0
      ? server.headers
      : null;

  return (
    <div className="ag-entity">
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" aria-hidden="true">
            <Server />
            <span className="ag-server-dot" data-tone={isConnected ? "success" : "danger"} />
          </span>
          <div className="min-w-0">
            <h3 className="ag-entity-name" title={name}>{name}</h3>
            <div className="ag-entity-sub">
              <span className="ag-status" data-tone={isConnected ? "success" : "danger"}>
                <span className="ag-status-dot" aria-hidden="true" />
                {isConnected ? "Connected" : "Disconnected"}
              </span>
              {!isEnabled && (
                <span className="ag-chip" data-tone="muted">Disabled</span>
              )}
            </div>
          </div>
        </div>
        <div className="ag-entity-actions">
          <button type="button" onClick={() => onEdit(server)} disabled={isBusy} className="ag-icon-button" title="Edit server">
            <Pencil />
          </button>
          <button type="button" onClick={handleTestSaved} disabled={isBusy || testing} className="ag-icon-button" data-tone="primary" title="Test connection">
            {testing ? <Loader2 className="animate-spin" /> : <FlaskConical />}
          </button>
          <button type="button" onClick={() => { setActiveAction("toggle"); onToggleEnabled(name, isEnabled); }} disabled={isBusy} className="ag-icon-button" data-tone={isEnabled ? "success" : undefined} title={isEnabled ? "Disable server" : "Enable server"}>
            {isBusy && activeAction === "toggle" ? <Loader2 className="animate-spin" /> : isEnabled ? <ToggleRight /> : <ToggleLeft />}
          </button>
          {isConnected ? (
            <button type="button" onClick={() => { setActiveAction("reconnect"); onReconnect(name); }} disabled={isBusy} className="ag-icon-button" data-tone="primary" title="Reconnect">
              {isBusy && activeAction === "reconnect" ? <Loader2 className="animate-spin" /> : <RotateCw />}
            </button>
          ) : (
            <button type="button" onClick={() => { setActiveAction("connect"); onConnect(name); }} disabled={isBusy || !isEnabled} className="ag-icon-button" data-tone="success" title="Connect">
              {isBusy && activeAction === "connect" ? <Loader2 className="animate-spin" /> : <Plug />}
            </button>
          )}
          <button type="button" onClick={() => onDelete(server)} disabled={isBusy} className="ag-icon-button" data-tone="danger" title="Delete server">
            <Trash2 />
          </button>
        </div>
      </div>

      <div className="ag-entity-body">
        {isConnected && server.connectedAt && (
          <p className="ag-hint">Connected since {formatTimestamp(server.connectedAt)}</p>
        )}

        <div className="ag-server-metrics ag-metrics-3">
          {[
            { label: "Tools", value: server.toolCount || 0, tone: "primary" },
            { label: "Resources", value: server.resourceCount || 0, tone: "violet" },
            { label: "Prompts", value: server.promptCount || 0, tone: "amber" },
          ].map((cap) => (
            <div key={cap.label} className="ag-metric" data-tone={cap.tone}>
              <span className="ag-metric-label">{cap.label}</span>
              <span className="ag-metric-value">{cap.value}</span>
            </div>
          ))}
        </div>

        <span className="ag-section-label">
          <Globe aria-hidden="true" />
          Network Configuration
        </span>

        <div className="ag-field-grid">
          <div className="ag-field">
            <span className="ag-field-label">Protocol Type</span>
            <span className="ag-field-value">{protocolType}</span>
          </div>
          <div className="ag-field">
            <span className="ag-field-label">Auto-Connect</span>
            <span className="ag-field-value" data-tone={server.autoConnect !== false ? "success" : "muted"}>
              {server.autoConnect !== false ? "Enabled" : "Disabled"}
            </span>
          </div>
          <div className="ag-field">
            <span className="ag-field-label">Timeout</span>
            <span className="ag-field-value">{server.timeoutSeconds || 30}s</span>
          </div>
          <div className="ag-field">
            <span className="ag-field-label">Connected</span>
            <span className="ag-field-value">{connectedLabel}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="ag-field-label">Endpoint URL</span>
          <div className="ag-code">
            <span>{server.url || "-"}</span>
            {server.url && (
              <button
                type="button"
                onClick={async () => {
                  await copyToClipboard(server.url || "");
                  toast({ title: "Copied to clipboard" });
                }}
                className="ag-copy"
                title="Copy"
              >
                <Copy />
              </button>
            )}
          </div>
        </div>

        {(headers || server.serverConfig) && (
          <>
            <span className="ag-section-label">
              <Key aria-hidden="true" />
              Authorization Security
            </span>
            <div className={cn("grid gap-4", headers && server.serverConfig ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
              {headers && (
                <div className="flex flex-col gap-2 min-w-0">
                  <span className="ag-field-label">Custom Headers</span>
                  <div className="ag-code">
                    <div className="space-y-1">
                      {Object.entries(headers).map(([k, v]) => {
                        const val = String(v);
                        const redacted = val.length > 20
                          ? val.substring(0, 4) + "***REDACTED***" + val.substring(val.length - 4)
                          : val;
                        return (
                          <p key={k} className="break-all">
                            <span className="text-muted-foreground">{k}:</span>{" "}
                            <span className="text-amber-600 dark:text-amber-400">{redacted}</span>
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {server.serverConfig && (
                <div className="flex flex-col gap-2 min-w-0">
                  <span className="ag-field-label">Server Config (JSON)</span>
                  <div className="ag-code">
                    <div className="ag-code-scroll">
                      {typeof server.serverConfig === "string"
                        ? server.serverConfig
                        : JSON.stringify(server.serverConfig, null, 2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


interface ServerFormDialogProps {
  open: boolean;
  editingServer: McpServerConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

function ServerFormDialog({ open, editingServer, onClose, onSaved }: ServerFormDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<ServerFormState>(EMPTY_SERVER_FORM);
  const [initialHeaders, setInitialHeaders] = useState("");
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open) {
      setTestResult(null);
      if (editingServer) {
        const s = editingServer as any;
        const headersStr =
          s.headers && Object.keys(s.headers).length > 0 ? JSON.stringify(s.headers, null, 2) : "";
        // On edit, existing (masked) headers default to the "Custom" method so nothing is silently dropped.
        setForm({
          ...EMPTY_SERVER_FORM,
          name: s.serverName || s.name || "",
          url: s.url ?? "",
          timeoutSeconds: s.timeoutSeconds ?? 30,
          enabled: s.enabled !== false,
          autoConnect: s.autoConnect !== false,
          authMode: headersStr ? "custom" : "none",
          headers: headersStr,
          serverConfig: s.serverConfig ? JSON.stringify(s.serverConfig, null, 2) : "",
        });
        setInitialHeaders(headersStr);
      } else {
        setForm(EMPTY_SERVER_FORM);
        setInitialHeaders("");
      }
    }
  }, [open, editingServer]);

  const isEdit = editingServer !== null;

  // Translate the selected auth preset into { headers, url } from the form inputs. Each method maps onto the
  // gateway's existing header/URL model, so no backend change is needed. Throws if the "Custom" JSON is malformed.
  const collectAuth = (baseUrl: string): { headers: Record<string, string>; url: string } => {
    let headers: Record<string, string> = {};
    let url = baseUrl;
    if (form.authMode === "bearer") {
      const t = form.authToken.trim();
      if (t) headers["Authorization"] = "Bearer " + t;
    } else if (form.authMode === "apikey") {
      const hn = form.authHeaderName.trim();
      const hv = form.authHeaderValue.trim();
      if (hn && hv) headers[hn] = hv;
    } else if (form.authMode === "urlparam") {
      const pn = form.authParamName.trim();
      const pv = form.authParamValue.trim();
      if (pn && pv) {
        const sep = baseUrl.indexOf("?") === -1 ? "?" : "&";
        url = baseUrl + sep + encodeURIComponent(pn) + "=" + encodeURIComponent(pv);
      }
    } else if (form.authMode === "custom") {
      const raw = form.headers.trim();
      if (raw) headers = JSON.parse(raw); // may throw
    }
    return { headers, url };
  };

  // Test the UNSAVED form config: build the same body Save would, but hit the dry-run probe endpoint.
  const testConfigForm = async () => {
    let url = form.url.trim();
    if (!url) {
      toast({ title: "Enter a Target URL to test", variant: "destructive" });
      return;
    }
    if (!/^https?:\/\//.test(url)) {
      toast({ title: "URL must start with http:// or https://", variant: "destructive" });
      return;
    }
    let headers: Record<string, string> = {};
    try {
      const auth = collectAuth(url);
      headers = auth.headers;
      url = auth.url;
    } catch {
      toast({ title: "Custom Headers JSON is invalid", variant: "destructive" });
      return;
    }
    let serverConfig: any = null;
    if (form.serverConfig.trim()) {
      try { serverConfig = JSON.parse(form.serverConfig); }
      catch { toast({ title: "Server Config JSON is invalid", variant: "destructive" }); return; }
    }
    const body: any = {
      serverName: form.name.trim() || "test",
      type: "http",
      url,
      headers,
      ...(serverConfig ? { serverConfig } : {}),
      timeoutSeconds: form.timeoutSeconds,
      enabled: true,
      autoConnect: false,
    };
    setTesting(true);
    setTestResult(null);
    try {
      const r = await GatewayServerConfigApi.test(body);
      setTestResult(r);
    } catch (e: any) {
      setTestResult({ ok: false, error: apiError(e) });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) {
      toast({ title: "Server name is required", variant: "destructive" });
      return;
    }
    // backend @Pattern ^[a-zA-Z0-9_-]+$ @Size(max=128)
    if (!/^[a-zA-Z0-9_-]+$/.test(name) || name.length > 128) {
      toast({ title: "Invalid server name", description: "Use letters, numbers, underscore or hyphen (max 128 chars).", variant: "destructive" });
      return;
    }
    if (!url) {
      toast({ title: "URL is required", variant: "destructive" });
      return;
    }
    if (!/^https?:\/\//.test(url)) {
      toast({ title: "URL must start with http:// or https://", variant: "destructive" });
      return;
    }
    if (url.length > 1024) {
      toast({ title: "URL is too long", description: "Max 1024 characters.", variant: "destructive" });
      return;
    }

    // Auth preset → { headers, url } (Bearer/API-key build headers; URL-param rewrites the URL).
    // For "custom" we keep the omit-on-unchanged rule: sending {} on a PUT wipes stored headers, so on an
    // unchanged edit we omit the key entirely (also avoids re-sending masked/redacted values).
    let headers: Record<string, string> | undefined;
    let finalUrl = url;
    if (form.authMode === "custom") {
      const headersChanged = !isEdit || form.headers.trim() !== initialHeaders.trim();
      if (headersChanged) {
        headers = {};
        if (form.headers.trim()) {
          try { headers = JSON.parse(form.headers); }
          catch { toast({ title: "Headers JSON is invalid", variant: "destructive" }); return; }
        }
      }
    } else {
      const auth = collectAuth(url);
      headers = auth.headers;
      finalUrl = auth.url;
    }

    let serverConfig: any = null;
    if (form.serverConfig.trim()) {
      try { serverConfig = JSON.parse(form.serverConfig); }
      catch { toast({ title: "Server Config JSON is invalid", variant: "destructive" }); return; }
    }

    const body: any = {
      serverName: name,
      type: "http",
      url: finalUrl,
      ...(headers !== undefined ? { headers } : {}),
      ...(serverConfig ? { serverConfig } : {}),
      timeoutSeconds: form.timeoutSeconds,
      enabled: form.enabled,
      autoConnect: form.autoConnect,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await GatewayServerConfigApi.update(editingServer!.name, body);
        toast({ title: `Server '${form.name}' updated` });
      } else {
        await GatewayServerConfigApi.create(body);
        toast({ title: `Server '${form.name}' created` });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <Server />
            </span>
            <div>
              <DialogTitle>{isEdit ? "Edit Server Configuration" : "Add MCP Server"}</DialogTitle>
              <DialogDescription>
                {isEdit
                  ? "Update the connection parameters for this MCP server."
                  : "Configure a new MCP server connection for the gateway."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="management-dialog-body">
          <section className="management-form-section">
            <span className="management-section-title">
              <Plug />
              General Connection
            </span>
            <div className="ag-form-grid">
              <div className="management-form-field">
                <label className="ag-label">
                  Server Name <span className="ag-required">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  disabled={isEdit}
                  placeholder="e.g. github"
                  className="ag-input"
                />
                <span className="ag-hint">Alphanumeric, underscore, or hyphen only</span>
              </div>

              <div className="management-form-field">
                <label className="ag-label">Type</label>
                <div className="ag-input flex items-center text-muted-foreground" aria-disabled="true">
                  HTTP
                </div>
              </div>

              <div className="management-form-field ag-span">
                <label className="ag-label">
                  Target URL <span className="ag-required">*</span>
                </label>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  placeholder="https://api.example.com/mcp/"
                  className="ag-input"
                />
              </div>

              <div className="management-form-field">
                <label className="ag-label">Timeout (seconds)</label>
                <input
                  type="number"
                  value={form.timeoutSeconds}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      timeoutSeconds: Math.max(5, Math.min(300, Number(e.target.value) || 30)),
                    }))
                  }
                  min={5}
                  max={300}
                  className="ag-input"
                />
              </div>
            </div>
          </section>

          <section className="management-form-section">
            <span className="management-section-title">
              <ShieldCheck />
              Execution Policy
            </span>
            <div className="flex flex-col gap-3.5">
              <div className="ag-toggle-row">
                <div>
                  <p className="ag-toggle-title">Server Enabled</p>
                  <p className="ag-toggle-text">Allow incoming traffic</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.enabled}
                  onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
                  className="ag-switch"
                >
                  <span />
                </button>
              </div>

              <div className="ag-toggle-row">
                <div>
                  <p className="ag-toggle-title">Auto-connect</p>
                  <p className="ag-toggle-text">Initialize on startup</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.autoConnect}
                  onClick={() => setForm((f) => ({ ...f, autoConnect: !f.autoConnect }))}
                  className="ag-switch"
                >
                  <span />
                </button>
              </div>
            </div>
          </section>

          <section className="management-form-section">
            <span className="management-section-title">
              <Key />
              Authentication
            </span>
            <div className="flex flex-col gap-4">
              <div className="management-form-field">
                <label className="ag-label">Auth Method</label>
                <Select value={form.authMode} onValueChange={(v) => setForm((f) => ({ ...f, authMode: v as AuthMode }))}>
                  <SelectTrigger className="ag-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="bearer">Bearer Token</SelectItem>
                    <SelectItem value="apikey">API Key (header)</SelectItem>
                    <SelectItem value="urlparam">API Key (URL param)</SelectItem>
                    <SelectItem value="custom">Custom Headers (JSON)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="ag-hint">
                  Secrets are encrypted at rest. Use <code>{"${env:VAR}"}</code> to read from an env var.
                </p>
              </div>

              {form.authMode === "none" && (
                <p className="ag-hint">No authentication headers will be sent to this server.</p>
              )}
              {form.authMode === "bearer" && (
                <div className="management-form-field">
                  <label className="ag-label">Bearer Token</label>
                  <input type="text" value={form.authToken} onChange={(e) => setForm((f) => ({ ...f, authToken: e.target.value }))} placeholder={"paste token or ${env:MY_TOKEN}"} className="ag-input" />
                  <p className="ag-hint">Sent as <code>{"Authorization: Bearer <token>"}</code>.</p>
                </div>
              )}
              {form.authMode === "apikey" && (
                <div className="ag-form-grid">
                  <div className="management-form-field">
                    <label className="ag-label">Header Name</label>
                    <input type="text" value={form.authHeaderName} onChange={(e) => setForm((f) => ({ ...f, authHeaderName: e.target.value }))} className="ag-input" />
                  </div>
                  <div className="management-form-field">
                    <label className="ag-label">API Key</label>
                    <input type="text" value={form.authHeaderValue} onChange={(e) => setForm((f) => ({ ...f, authHeaderValue: e.target.value }))} placeholder={"paste key or ${env:MY_KEY}"} className="ag-input" />
                  </div>
                </div>
              )}
              {form.authMode === "urlparam" && (
                <div className="flex flex-col gap-4">
                  <div className="ag-form-grid">
                    <div className="management-form-field">
                      <label className="ag-label">Query Param Name</label>
                      <input type="text" value={form.authParamName} onChange={(e) => setForm((f) => ({ ...f, authParamName: e.target.value }))} className="ag-input" />
                    </div>
                    <div className="management-form-field">
                      <label className="ag-label">API Key</label>
                      <input type="text" value={form.authParamValue} onChange={(e) => setForm((f) => ({ ...f, authParamValue: e.target.value }))} placeholder="paste key" className="ag-input" />
                    </div>
                  </div>
                  <p className="ag-hint">Appended to the Target URL as <code>?name=value</code> when you save or test.</p>
                </div>
              )}
              {form.authMode === "custom" && (
                <div className="management-form-field">
                  <label className="ag-label">
                    Custom Headers (JSON) — use <code>{"${env:VAR_NAME}"}</code> for secrets
                  </label>
                  <textarea
                    value={form.headers}
                    onChange={(e) => setForm((f) => ({ ...f, headers: e.target.value }))}
                    rows={4}
                    placeholder={'{\n  "Authorization": "Bearer ${env:GITHUB_TOKEN}"\n}'}
                    className="ag-input"
                  />
                </div>
              )}
            </div>
          </section>

          <section className="management-form-section">
            <span className="management-section-title">
              <Server />
              Data Payload Configuration
            </span>
            <div className="management-form-field">
              <label className="ag-label">Server Config Schema (JSON, optional)</label>
              <textarea
                value={form.serverConfig}
                onChange={(e) => setForm((f) => ({ ...f, serverConfig: e.target.value }))}
                rows={3}
                placeholder={'{\n  "jiraUrl": "https://company.atlassian.net"\n}'}
                className="ag-input"
              />
            </div>
          </section>

          {testResult && (
            testResult.ok ? (
              <div className="ag-alert" data-tone="success">
                <CheckCircle />
                <span>
                  Connected to{" "}
                  <strong>
                    {testResult.serverInfoName
                      ? `${testResult.serverInfoName}${testResult.serverInfoVersion ? ` v${testResult.serverInfoVersion}` : ""}`
                      : "MCP server"}
                  </strong>
                  {" — "}
                  {[
                    `${testResult.toolCount ?? 0} tools`,
                    testResult.resourceCount ? `${testResult.resourceCount} resources` : null,
                    testResult.promptCount ? `${testResult.promptCount} prompts` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}{" "}
                  <span className="opacity-70">({testResult.latencyMs}ms)</span>
                </span>
              </div>
            ) : (
              <div className="ag-alert" data-tone="danger">
                <XCircle />
                <span>{testResult.error || "Connection failed"}</span>
              </div>
            )
          )}
        </div>

        <DialogFooter className="management-dialog-footer ag-footer-split">
          <Button
            variant="outline"
            onClick={testConfigForm}
            disabled={saving || testing}
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
          <div className="ag-footer-group">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="endpoint-primary-action"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? "Update Server" : "Add Server"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


interface DeleteConfirmDialogProps {
  open: boolean;
  serverName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

function DeleteConfirmDialog({
  open,
  serverName,
  onClose,
  onConfirm,
  loading,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog ag-confirm">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
              <Trash2 />
            </span>
            <div>
              <DialogTitle>Delete Server</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{serverName}</strong>? This action cannot
                be undone.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter className="management-dialog-footer">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="ag-danger-button"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}

function McpServersTab({ tabs }: { tabs: ReactNode }) {
  const { toast } = useToast();
  const [servers, setServers] = useState<McpServerConfig[]>([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<McpServerConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<McpServerConfig | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadServers = useCallback(async () => {
    setLoadingServers(true);
    try {
      const result = await GatewayServerConfigApi.list();
      setServers(Array.isArray(result) ? result : []);
    } catch (e: any) {
      toast({
        title: "Failed to load server configurations",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoadingServers(false);
    }
  }, [toast]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleEdit = (server: McpServerConfig) => {
    setEditingServer(server);
    setFormOpen(true);
  };

  const handleAdd = () => {
    setEditingServer(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingServer(null);
  };

  const handleDeleteRequest = (server: McpServerConfig) => {
    setDeleteTarget(server);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await GatewayServerConfigApi.remove(deleteTarget.name);
      toast({ title: `Server '${deleteTarget.name}' deleted` });
      setDeleteTarget(null);
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleConnect = async (name: string) => {
    setActionLoading(name);
    try {
      await GatewayServerConfigApi.connect(name);
      toast({ title: `Server '${name}' connected` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Connect failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReconnect = async (name: string) => {
    setActionLoading(name);
    try {
      await GatewayServerConfigApi.reconnect(name);
      toast({ title: `Server '${name}' reconnected` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Reconnect failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // One-click enable/disable (no full edit); disabling also disconnects any live session server-side.
  const handleToggleEnabled = async (name: string, isEnabled: boolean) => {
    setActionLoading(name);
    try {
      if (isEnabled) await GatewayServerConfigApi.disable(name);
      else await GatewayServerConfigApi.enable(name);
      toast({ title: `Server '${name}' ${isEnabled ? "disabled" : "enabled"}` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Toggle failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredServers = priorityFilter(servers, search, (s) => s.name || "");

  return (
    <>
      <div className="ag-card ag-toolbar ag-nav">
        {tabs}
        <div className="ag-toolbar-group">
          {servers.length > 0 && (
            <span className="ag-summary">
              <PlugZap aria-hidden="true" />
              <span>
                <strong>{servers.filter((s) => s.connected).length}</strong> of <strong>{servers.length}</strong> servers connected
              </span>
            </span>
          )}
          {servers.length > 0 && (
            <div className="ag-search">
              <Search aria-hidden="true" />
              <input
                type="text"
                placeholder="Search servers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <Button
            variant="outline"
            onClick={loadServers}
            className="ag-button no-default-hover-elevate"
          >
            <RefreshCw />
            Refresh
          </Button>
          <Button
            onClick={handleAdd}
            className="ag-button ag-primary-button no-default-hover-elevate"
          >
            <Plus />
            Add Server
          </Button>
        </div>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {loadingServers ? (
            <div className="ag-card-grid">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="ag-skeleton h-72 rounded-2xl" />
              ))}
            </div>
          ) : servers.length === 0 ? (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Server />
              </span>
              <p className="ag-empty-title">No server configurations</p>
              <p>Click &ldquo;Add Server&rdquo; to configure your first MCP server</p>
              <Button
                onClick={handleAdd}
                className="ag-button ag-primary-button no-default-hover-elevate mt-2"
              >
                <Plus />
                Add Server
              </Button>
            </div>
          ) : filteredServers.length === 0 ? (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Search />
              </span>
              <p>No servers match &ldquo;{search}&rdquo;</p>
            </div>
          ) : (
            <div className="ag-card-grid">
              {filteredServers.map((server) => (
                <ServerCard
                  key={server.name}
                  server={server}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                  onConnect={handleConnect}
                  onReconnect={handleReconnect}
                  onToggleEnabled={handleToggleEnabled}
                  actionLoading={actionLoading}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <ServerFormDialog
        open={formOpen}
        editingServer={editingServer}
        onClose={handleFormClose}
        onSaved={loadServers}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        serverName={deleteTarget?.name ?? ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
      />
    </>
  );
}


interface AuthConfigCardProps {
  config: any;
  onEdit: () => void;
  onDelete: () => void;
  onRefreshJwks: () => void;
  actionLoading: boolean;
}

function AuthConfigCard({
  config,
  onEdit,
  onDelete,
  onRefreshJwks,
  actionLoading,
}: AuthConfigCardProps) {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast({ title: "Copied to clipboard" });
    }
  };

  const mode = config.effectiveMode || config.authMode || "none";
  const isOAuth2 = mode === "oauth2";
  const source = config.configSource || "default";
  const idpName = config.idpDisplayName || detectIdpType(config.issuerUri);
  const isDbSource = source === "database";

  return (
    <div className="ag-entity">
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" aria-hidden="true">
            {idpIcon(config.issuerUri)}
            <span className="ag-server-dot" data-tone={isOAuth2 ? "success" : "danger"} />
          </span>
          <div className="min-w-0">
            <h3 className="ag-entity-name" title={idpName}>{idpName}</h3>
            <div className="ag-entity-sub">
              <span className="ag-status" data-tone={isOAuth2 ? "success" : "danger"}>
                <span className="ag-status-dot" aria-hidden="true" />
                {isOAuth2 ? "Active" : "Disabled"}
              </span>
              <span className="ag-chip" data-tone="muted" title="Configuration source">
                {isDbSource ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="14" x="2" y="3" rx="2" /><line x1="8" x2="16" y1="21" y2="21" /><line x1="12" x2="12" y1="17" y2="21" />
                  </svg>
                )}
                {isDbSource ? "Database" : "Environment"}
              </span>
            </div>
          </div>
        </div>
        <div className="ag-entity-actions">
          {isDbSource ? (
            <>
              <button type="button" onClick={onEdit} disabled={actionLoading} className="ag-icon-button" title="Edit configuration">
                <Pencil />
              </button>
              <button type="button" onClick={onRefreshJwks} disabled={actionLoading} className="ag-icon-button" data-tone="primary" title="Refresh JWKS">
                {actionLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
              </button>
              <button type="button" onClick={onDelete} disabled={actionLoading} className="ag-icon-button" data-tone="danger" title="Delete configuration">
                <Trash2 />
              </button>
            </>
          ) : (
            <button type="button" onClick={onEdit} disabled={actionLoading} className="ag-icon-button" data-tone="primary" title="Save to database">
              <Settings />
            </button>
          )}
        </div>
      </div>

      <div className="ag-entity-body">
        <div className="ag-auth-layout">
          <div className="flex flex-col gap-3 min-w-0">
            <span className="ag-section-label">
              <Settings aria-hidden="true" />
              Policy
            </span>
            <div className="ag-field-grid">
              {[
                { label: "Mode", value: mode.toUpperCase() },
                { label: "Classification", value: (config.tokenClassificationMode || "strict").toUpperCase() },
                { label: "Grace Period", value: `${config.gracePeriodMinutes || 30} min` },
                ...(config.createdAt ? [{ label: "Created", value: formatTimestamp(config.createdAt) }] : []),
              ].map((stat) => (
                <div key={stat.label} className="ag-field">
                  <span className="ag-field-label">{stat.label}</span>
                  <span className="ag-field-value" title={stat.value}>{stat.value}</span>
                </div>
              ))}
            </div>

            {config.supportedScopes && config.supportedScopes.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="ag-field-label">Scopes</span>
                <div className="flex flex-wrap gap-1.5">
                  {(Array.isArray(config.supportedScopes) ? config.supportedScopes : String(config.supportedScopes).split(",").map((s: string) => s.trim()).filter(Boolean)).map((scope: string) => (
                    <span key={scope} className="ag-chip">
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3 min-w-0">
            <span className="ag-section-label">
              <Globe aria-hidden="true" />
              Identity Provider
            </span>

            {config.issuerUri && (
              <div className="flex flex-col gap-2">
                <span className="ag-field-label">Issuer URI</span>
                <div className="ag-code">
                  <span>{config.issuerUri}</span>
                  <button type="button" onClick={() => handleCopy(config.issuerUri, "issuer")} className="ag-copy" title="Copy">
                    {copiedField === "issuer" ? <CheckCircle className="text-emerald-500" /> : <Copy />}
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <span className="ag-field-label">JWKS URI</span>
              <div className="ag-code">
                <span>{config.jwksUri || "auto-discovered"}</span>
                {config.jwksUri && (
                  <button type="button" onClick={() => handleCopy(config.jwksUri, "jwks")} className="ag-copy" title="Copy">
                    {copiedField === "jwks" ? <CheckCircle className="text-emerald-500" /> : <Copy />}
                  </button>
                )}
              </div>
            </div>

            {(config.jwtDecoderActive || config.gracePeriodActive) && (
              <div className="flex flex-wrap gap-2">
                {config.jwtDecoderActive && (
                  <span className="ag-chip" data-tone="success">
                    <CheckCircle /> JWT Decoder active
                  </span>
                )}
                {config.gracePeriodActive && (
                  <span className="ag-chip" data-tone="amber">
                    <Clock /> Grace period — {config.gracePeriodRemainingMinutes ?? "?"} min
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <StsKeysPanel embedded />
    </div>
  );
}


interface AuthConfigFormProps {
  existing: any | null;
  onSaved: () => void;
  onCancel: () => void;
}

function AuthConfigForm({ existing, onSaved, onCancel }: AuthConfigFormProps) {
  const { toast } = useToast();
  const isEdit = existing !== null;

  const [form, setForm] = useState<AuthFormState>(() => {
    if (existing) {
      return {
        idpDisplayName: existing.idpDisplayName ?? "",
        authMode: existing.authMode ?? "oauth2",
        issuerUri: existing.issuerUri ?? "",
        jwksUri: existing.jwksUri ?? "",
        authorizationEndpoint: existing.authorizationEndpoint ?? "",
        tokenEndpoint: existing.tokenEndpoint ?? "",
        tokenClassificationMode: existing.tokenClassificationMode ?? "jwt-signals",
        gracePeriodMinutes: String(existing.gracePeriodMinutes ?? "30"),
        supportedScopes: Array.isArray(existing.supportedScopes)
          ? existing.supportedScopes.join(", ")
          : (existing.supportedScopes ?? ""),
      };
    }
    return EMPTY_AUTH_FORM;
  });

  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [validation, setValidation] = useState<ValidationResult>({ state: "idle" });

  const setField = (key: keyof AuthFormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleIssuerChange = (val: string) => {
    setForm((f) => ({
      ...f,
      issuerUri: val,
      jwksUri: "",
      authorizationEndpoint: "",
      tokenEndpoint: "",
    }));
    setValidation({ state: "idle" });
  };

  const handleDiscover = async () => {
    if (!form.issuerUri.trim()) {
      toast({ title: "Enter an Issuer URI first", variant: "destructive" });
      return;
    }
    setDiscovering(true);
    try {
      const result = await GatewayAuthConfigApi.discover({ issuerUri: form.issuerUri.trim() });
      if (result.error) {
        toast({
          title: "Discovery failed",
          description: result.error,
          variant: "destructive",
        });
        return;
      }
      setForm((f) => ({
        ...f,
        jwksUri: result.jwksUri ?? f.jwksUri,
        authorizationEndpoint: result.authorizationEndpoint ?? f.authorizationEndpoint,
        tokenEndpoint: result.tokenEndpoint ?? f.tokenEndpoint,
      }));
      toast({ title: "Endpoints discovered successfully" });
    } catch (e: any) {
      toast({
        title: "Discovery failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDiscovering(false);
    }
  };

  const handleValidate = async () => {
    if (!form.issuerUri.trim()) {
      toast({ title: "Enter an Issuer URI first", variant: "destructive" });
      return;
    }
    setValidation({ state: "loading" });
    try {
      const result = await GatewayAuthConfigApi.validate({
        issuerUri: form.issuerUri.trim(),
        jwksUri: form.jwksUri.trim() || undefined,
      });
      if (result.jwksReachable) {
        setValidation({
          state: "success",
          message: `${result.jwksKeyCount} keys found`,
          jwksKeyCount: result.jwksKeyCount,
          jwksLatencyMs: result.jwksLatencyMs,
        });
      } else {
        setValidation({
          state: "error",
          message: result.errorMessage || "Validation failed",
        });
      }
    } catch (e: any) {
      setValidation({ state: "error", message: apiError(e) });
    }
  };

  const handleSave = async () => {
    if (!form.issuerUri.trim() && form.authMode === "oauth2") {
      toast({ title: "Issuer URI is required for OAuth2 mode", variant: "destructive" });
      return;
    }
    const body = {
      authMode: form.authMode,
      idpDisplayName: form.idpDisplayName.trim() || undefined,
      issuerUri: form.issuerUri.trim() || undefined,
      jwksUri: form.jwksUri.trim() || undefined,
      authorizationEndpoint: form.authorizationEndpoint.trim() || undefined,
      tokenEndpoint: form.tokenEndpoint.trim() || undefined,
      tokenClassificationMode: form.tokenClassificationMode,
      gracePeriodMinutes: parseInt(form.gracePeriodMinutes, 10),
      // round-trip scopes so an edit doesn't wipe stored supportedScopes (backend nulls it if omitted)
      supportedScopes: form.supportedScopes.trim() || undefined,
      enabled: true,
    };
    setSaving(true);
    try {
      if (isEdit && existing?.id) {
        await GatewayAuthConfigApi.update(body);
        toast({ title: "Authentication config updated" });
      } else {
        await GatewayAuthConfigApi.create(body);
        toast({ title: "Authentication config created" });
      }
      onSaved();
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="ag-form-head">
        <div className="ag-card-heading">
          <span className="ag-heading-icon" aria-hidden="true">
            <ShieldCheck />
          </span>
          <div className="min-w-0">
            <h3 className="ag-card-title">Configure Authentication</h3>
            <p className="ag-card-description">
              Connect an Identity Provider to enable OAuth2 for AI agents.
            </p>
          </div>
        </div>
        <div className="ag-footer-group">
          <Button
            variant="outline"
            onClick={onCancel}
            className="ag-button no-default-hover-elevate"
          >
            Discard
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="ag-button ag-primary-button no-default-hover-elevate"
          >
            {saving && <Loader2 className="animate-spin" />}
            {isEdit ? "Update Configuration" : "Save Configuration"}
          </Button>
        </div>
      </div>

      <div className="ag-auth-form">
        <section className="ag-form-section">
          <span className="ag-section-label">
            <Globe aria-hidden="true" />
            Identity Provider
          </span>

          <div className="ag-form-grid">
            <div className="management-form-field">
              <label className="ag-label">IDP Display Name</label>
              <input
                type="text"
                value={form.idpDisplayName}
                onChange={(e) => setField("idpDisplayName", e.target.value)}
                placeholder="e.g. Corporate Okta"
                className="ag-input"
              />
            </div>
            <div className="management-form-field">
              <label className="ag-label">
                Auth Mode <span className="ag-required">*</span>
              </label>
              <Select value={form.authMode} onValueChange={(v) => setField("authMode", v)}>
                <SelectTrigger className="ag-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="oauth2">OIDC / OAuth2.0</SelectItem>
                  <SelectItem value="none">None (disabled)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="management-form-field">
            <label className="ag-label">
              Issuer URI <span className="ag-required">*</span>
            </label>
            <input
              type="text"
              value={form.issuerUri}
              onChange={(e) => handleIssuerChange(e.target.value)}
              placeholder="https://auth.company.com"
              className="ag-input"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleDiscover}
                disabled={discovering || !form.issuerUri.trim()}
                className="ag-button ag-compact no-default-hover-elevate"
              >
                {discovering ? <Loader2 className="animate-spin" /> : <Search />}
                Discover
              </Button>
              <Button
                variant="outline"
                onClick={handleValidate}
                disabled={validation.state === "loading" || !form.issuerUri.trim()}
                className="ag-button ag-compact no-default-hover-elevate"
              >
                {validation.state === "loading" ? <Loader2 className="animate-spin" /> : <CheckCircle />}
                Validate
              </Button>
            </div>

            {validation.state === "success" && (
              <div className="ag-alert" data-tone="success">
                <CheckCircle />
                <span>
                  JWKS reachable &mdash; {validation.jwksKeyCount} keys found &mdash;{" "}
                  {validation.jwksLatencyMs}ms latency
                </span>
              </div>
            )}
            {validation.state === "error" && (
              <div className="ag-alert" data-tone="danger">
                <XCircle />
                <span>{validation.message}</span>
              </div>
            )}

            <div className="ag-alert" data-tone="amber">
              <AlertTriangle />
              <span>Issuer URI must match the Frontend URL in your Identity Provider (e.g. Keycloak&rsquo;s Frontend URL, Azure AD&rsquo;s Issuer URI).</span>
            </div>
          </div>

          <div className="management-form-field">
            <label className="ag-label">JWKS URI</label>
            <input
              type="text"
              value={form.jwksUri}
              onChange={(e) => setField("jwksUri", e.target.value)}
              placeholder="https://auth.company.com/.well-known/jwks.json"
              className="ag-input"
            />
          </div>

          <div className="ag-form-divider" />

          <span className="ag-section-label">OAuth2 Endpoints</span>
          <div className="management-form-field">
            <label className="ag-label">Authorization Endpoint</label>
            <input
              type="text"
              value={form.authorizationEndpoint}
              onChange={(e) => setField("authorizationEndpoint", e.target.value)}
              placeholder="https://auth.company.com/v1/authorize"
              className="ag-input"
            />
          </div>
          <div className="management-form-field">
            <label className="ag-label">Token Endpoint</label>
            <input
              type="text"
              value={form.tokenEndpoint}
              onChange={(e) => setField("tokenEndpoint", e.target.value)}
              placeholder="https://auth.company.com/v1/token"
              className="ag-input"
            />
          </div>
        </section>

        <section className="ag-form-section">
          <span className="ag-section-label">
            <Settings aria-hidden="true" />
            Execution Policy
          </span>

          <div className="management-form-field">
            <label className="ag-label">Token Classification</label>
            <Select
              value={form.tokenClassificationMode}
              onValueChange={(v) => setField("tokenClassificationMode", v)}
            >
              <SelectTrigger className="ag-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {TOKEN_CLASSIFICATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="ag-hint">
              {
                TOKEN_CLASSIFICATION_OPTIONS.find(
                  (o) => o.value === form.tokenClassificationMode
                )?.description
              }
            </p>
          </div>

          <div className="management-form-field">
            <label className="ag-label">Grace Period</label>
            <Select
              value={form.gracePeriodMinutes}
              onValueChange={(v) => setField("gracePeriodMinutes", v)}
            >
              <SelectTrigger className="ag-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="5">5 minutes</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="480">8 hours</SelectItem>
                <SelectItem value="1440">24 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="management-form-field">
            <label className="ag-label">Supported Scopes</label>
            <input
              type="text"
              value={form.supportedScopes}
              onChange={(e) => setField("supportedScopes", e.target.value)}
              placeholder="openid, email, profile"
              className="ag-input"
            />
            <p className="ag-hint">Comma-separated OAuth2 scopes requested from the IdP.</p>
          </div>

          <div className="ag-field ag-field-inline mt-auto">
            <span className="ag-field-label">Policy Health Check</span>
            <span className="ag-chip" data-tone="success">Active</span>
          </div>
        </section>
      </div>
    </div>
  );
}


// STS signing keys (OBO) panel — mirrors the OG Auth tab's "STS Signing Keys" section.
// `embedded` renders it as a section inside the auth card (no own card chrome).
function StsKeysPanel({ embedded = false }: { embedded?: boolean }) {
  const { toast } = useToast();
  const [keys, setKeys] = useState<any[]>([]);
  const [policy, setPolicy] = useState<{ autoRotate?: boolean; intervalDays?: number; nextRotationAt?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [intervalDays, setIntervalDays] = useState("90");
  const [keysExpanded, setKeysExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, p] = await Promise.all([
        GatewayStsApi.keys().catch(() => []),
        GatewayStsApi.rotationPolicy().catch(() => null),
      ]);
      setKeys(Array.isArray(k) ? k : (k?.keys || []));
      if (p) {
        setPolicy(p);
        setAutoRotate(!!p.autoRotate);
        setIntervalDays(String(p.intervalDays ?? 90));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRotate = async () => {
    setRotating(true);
    try {
      const res = await GatewayStsApi.rotate();
      const newKid = res?.newKid ? ` (new kid ${String(res.newKid).slice(0, 8)}…)` : "";
      toast({ title: `STS signing key rotated${newKid}` });
      setConfirmRotate(false);
      await load();
    } catch (e: any) {
      toast({ title: "Rotation failed", description: apiError(e), variant: "destructive" });
    } finally {
      setRotating(false);
    }
  };

  const handleSavePolicy = async () => {
    setSavingPolicy(true);
    try {
      const days = Math.max(1, parseInt(intervalDays, 10) || 1);
      await GatewayStsApi.setRotationPolicy({ autoRotate, intervalDays: days });
      toast({ title: "Rotation policy saved" });
      await load();
    } catch (e: any) {
      toast({ title: "Save failed", description: apiError(e), variant: "destructive" });
    } finally {
      setSavingPolicy(false);
    }
  };

  // Next auto-rotation computed on the client from the last rotation (active key's
  // creation) + the current interval, rolled forward to the next future occurrence.
  // This updates immediately as the interval changes — no stale backend value.
  const nextRotationDate = (() => {
    // Prefer the backend's authoritative next-rotation timestamp when present; fall back to a client recompute.
    const authoritative = (policy as any)?.nextRotationAt;
    if (authoritative) {
      const t = new Date(authoritative).getTime();
      if (!isNaN(t)) return new Date(t);
    }
    const days = Math.max(1, parseInt(intervalDays, 10) || 1);
    const period = days * 86400000;
    const activeKey = keys.find((k) => k.status === "ACTIVE");
    const baseStr = activeKey?.createdAt || (policy as any)?.lastRotatedAt;
    const baseMs = baseStr ? new Date(baseStr).getTime() : Date.now();
    if (isNaN(baseMs)) return null;
    const now = Date.now();
    let next = baseMs + period;
    if (next <= now) next = now + (period - ((now - baseMs) % period)); // roll to next future slot
    return new Date(next);
  })();

  const RotateButton = (
    <Button
      variant="outline"
      onClick={() => setConfirmRotate(true)}
      disabled={rotating}
      className="ag-button ag-compact no-default-hover-elevate shrink-0"
    >
      {rotating ? <Loader2 className="animate-spin" /> : <RotateCw />}
      Rotate now
    </Button>
  );

  return (
    <div className={cn("ag-sts", embedded ? "ag-sts-embedded" : "ag-form-section")}>
      <div className="ag-card-heading">
        <span className="ag-heading-icon" aria-hidden="true">
          <ShieldCheck />
        </span>
        <div className="min-w-0">
          <p className="ag-card-title">STS Signing Keys (OBO)</p>
          <p className="ag-card-description">
            The gateway's own per-tenant keys that sign delegation (OBO) tokens. Public halves are served at{" "}
            <code className="ag-inline-code">/.well-known/sts/jwks.json</code>; private keys are encrypted at rest and never shown.
          </p>
        </div>
      </div>

      {loading ? (
        <Skeleton className="ag-skeleton h-20 w-full rounded-xl" />
      ) : keys.length === 0 ? (
        <div className="ag-hint">
          No STS signing keys yet — a per-tenant key is minted on the first OBO token.
        </div>
      ) : (() => {
        const sorted = [...keys].sort((a, b) => {
          const aActive = a.status === "ACTIVE";
          const bActive = b.status === "ACTIVE";
          if (aActive !== bActive) return aActive ? -1 : 1;
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });
        const INITIAL = 3;
        const visible = keysExpanded ? sorted : sorted.slice(0, INITIAL);
        const hiddenCount = sorted.length - INITIAL;
        return (
          <div className="flex flex-col gap-2">
            {visible.map((k) => {
              const active = k.status === "ACTIVE";
              const retired = k.status === "RETIRED";
              const retiring = k.status === "RETIRING";
              const tone = active ? "success" : retiring ? "amber" : "muted";
              return (
                <div
                  key={k.kid}
                  className={cn("ag-key-row", retired && "opacity-60")}
                  data-tone={tone}
                >
                  <span className="ag-list-icon" aria-hidden="true">
                    <Key />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="ag-key-id">{k.kid}</div>
                    <div className="ag-hint">Created {k.createdAt ? formatTimestamp(k.createdAt) : "—"}</div>
                  </div>
                  <span className="ag-chip" data-tone={tone}>
                    {k.status || "—"}
                  </span>
                  {active && <div className="ml-2 shrink-0">{RotateButton}</div>}
                </div>
              );
            })}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setKeysExpanded((v) => !v)}
                className="ag-link-button"
              >
                {keysExpanded ? "Show less" : `Load ${hiddenCount} more`}
              </button>
            )}
          </div>
        );
      })()}

      <div className="ag-sts-policy">
        <div className="flex items-center gap-2 flex-wrap">
          <Checkbox
            id="sts-auto-rotate"
            checked={autoRotate}
            onCheckedChange={(v) => setAutoRotate(v === true)}
          />
          <label htmlFor="sts-auto-rotate" className="ag-toggle-text mt-0 cursor-pointer select-none">
            Auto-rotate every
          </label>
          <input
            type="number"
            min={1}
            value={intervalDays}
            onChange={(e) => setIntervalDays(e.target.value)}
            className="ag-input ag-number-input"
          />
          <span className="ag-toggle-text mt-0">days</span>
          <Button
            onClick={handleSavePolicy}
            disabled={savingPolicy}
            className="ag-button ag-primary-button ag-compact no-default-hover-elevate"
          >
            {savingPolicy ? <Loader2 className="animate-spin" /> : <Save />}
            Save
          </Button>
        </div>

        {!loading && keys.length === 0 && <div className="ml-auto">{RotateButton}</div>}

        <div className="basis-full ag-hint">
          {autoRotate
            ? nextRotationDate
              ? `Next auto-rotation: ${formatTimestamp(nextRotationDate.toISOString())}`
              : "Auto-rotation on."
            : "Auto-rotation off — rotate manually with the button."}
        </div>
      </div>

      <Dialog open={confirmRotate} onOpenChange={(v) => !rotating && setConfirmRotate(v)}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" aria-hidden="true">
                <RotateCw />
              </span>
              <div>
                <DialogTitle>Rotate STS signing key?</DialogTitle>
                <DialogDescription>
                  The current key is retired and a new key is minted. In-flight OBO tokens keep
                  verifying during the grace window, so no active sessions are interrupted.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => setConfirmRotate(false)}
              disabled={rotating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRotate}
              disabled={rotating}
              className="endpoint-primary-action"
            >
              {rotating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5 mr-1.5" />}
              Rotate now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AuthenticationTab({ tabs }: { tabs: ReactNode }) {
  const { toast } = useToast();
  const [authConfig, setAuthConfig] = useState<any | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editExisting, setEditExisting] = useState<any | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadAuthConfig = useCallback(async () => {
    setLoadingAuth(true);
    try {
      const result = await GatewayAuthConfigApi.get();
      setAuthConfig(result);
    } catch (e: any) {
      if (e?.status === 404 || e?.response?.status === 404) {
        setAuthConfig(null);
      } else {
        toast({
          title: "Failed to load auth config",
          description: apiError(e),
          variant: "destructive",
        });
      }
    } finally {
      setLoadingAuth(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAuthConfig();
  }, [loadAuthConfig]);

  const isOAuth2 = authConfig && (authConfig.effectiveMode === "oauth2" || authConfig.authMode === "oauth2");
  const isConfigured = authConfig && (isOAuth2 || authConfig.configSource === "database");

  const handleEdit = async () => {
    try {
      const config = await GatewayAuthConfigApi.get();
      setEditExisting(config);
      setShowForm(true);
    } catch (e: any) {
      toast({
        title: "Failed to load config for editing",
        description: apiError(e),
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await GatewayAuthConfigApi.remove();
      toast({ title: "Auth config deleted — reverted to defaults" });
      setDeleteDialogOpen(false);
      await loadAuthConfig();
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleRefreshJwks = async () => {
    setActionLoading(true);
    try {
      await GatewayAuthConfigApi.refreshJwks();
      toast({ title: "JWKS keys refreshed" });
    } catch (e: any) {
      toast({
        title: "JWKS refresh failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFormSaved = async () => {
    setShowForm(false);
    setEditExisting(null);
    await loadAuthConfig();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditExisting(null);
  };

  const renderShell = (content: ReactNode, summary?: ReactNode) => (
    <>
      <div className="ag-card ag-toolbar ag-nav">
        {tabs}
        {summary && <span className="ag-summary">{summary}</span>}
      </div>
      <div className="ag-card ag-content">
        <div className="ag-scroll">{content}</div>
      </div>
    </>
  );

  if (loadingAuth) {
    return renderShell(<Skeleton className="ag-skeleton h-64 w-full rounded-2xl" />);
  }

  if (showForm) {
    return renderShell(
      <AuthConfigForm
        existing={editExisting}
        onSaved={handleFormSaved}
        onCancel={handleFormCancel}
      />,
      <>
        <Pencil aria-hidden="true" />
        <span>{editExisting ? "Editing configuration" : "New configuration"}</span>
      </>
    );
  }

  if (!isConfigured) {
    return renderShell(
      <div className="flex flex-col gap-6">
        <div className="ag-empty">
          <span className="ag-empty-icon" aria-hidden="true">
            <ShieldCheck />
          </span>
          <p className="ag-empty-title">Authentication Not Configured</p>
          <p className="max-w-md">
            Configure an Identity Provider (Keycloak, Azure AD, Okta, Auth0) to enable OAuth2
            authentication for AI agents connecting to the gateway.
          </p>
          <Button
            onClick={() => {
              setEditExisting(null);
              setShowForm(true);
            }}
            className="ag-button ag-primary-button no-default-hover-elevate mt-2"
          >
            <Plus />
            Configure Identity Provider
          </Button>
        </div>

        <StsKeysPanel />
      </div>,
      <>
        <ShieldCheck aria-hidden="true" />
        <span>Not configured</span>
      </>
    );
  }

  return renderShell(
    <>
      <AuthConfigCard
        config={authConfig}
        onEdit={handleEdit}
        onDelete={() => setDeleteDialogOpen(true)}
        onRefreshJwks={handleRefreshJwks}
        actionLoading={actionLoading}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={(v) => !v && setDeleteDialogOpen(false)}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Trash2 />
              </span>
              <div>
                <DialogTitle>Delete Auth Config</DialogTitle>
                <DialogDescription>
                  Delete auth config? This will revert to environment variables or mode=none.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="ag-danger-button"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>,
    <>
      <ShieldCheck aria-hidden="true" />
      <span>
        <strong>{authConfig.idpDisplayName || detectIdpType(authConfig.issuerUri)}</strong> · {isOAuth2 ? "OAuth2 active" : "Disabled"}
      </span>
    </>
  );
}


export default function GatewayConfigPage() {
  const tabs = (
    <TabsList unstyled aria-label="Configuration sections" className="cea-tabs ag-tabs">
      <TabsTrigger unstyled value="servers" className="cea-tab">
        <Server className="shrink-0" />
        MCP Servers
      </TabsTrigger>
      <TabsTrigger unstyled value="auth" className="cea-tab">
        <ShieldCheck className="shrink-0" />
        Authentication
      </TabsTrigger>
    </TabsList>
  );

  return (
    <div className="ag-page">
      <Tabs defaultValue="servers" className="ag-tabs-root">
        <TabsContent value="servers" className="ag-panel mt-0">
          <McpServersTab tabs={tabs} />
        </TabsContent>

        <TabsContent value="auth" className="ag-panel mt-0">
          <AuthenticationTab tabs={tabs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
